import React, { FunctionComponent } from 'react';
import {
  Typography,
  Slider as MuiSlider,
  Tooltip,
  tooltipClasses,
  SliderProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Constants } from '../../../utils';
import InputText from '../InputText/InputText';
import useStyles from './Slider.styles';

// Custom styled MUI Slider
const CustomSlider = styled(MuiSlider)(({ theme }) => ({
  color: '#B0B0B0',
  height: 8,
  '& .MuiSlider-thumb': {
    height: 16,
    width: 16,
    marginLeft: -12,
    backgroundColor: '#1976d2',
  },
  '& .MuiSlider-track': {
    height: 6,
    borderRadius: 4,
    opacity: 0.4,
  },
  '& .MuiSlider-rail': {
    height: 6,
    borderRadius: 4,
    opacity: 0.4,
  },
  '& .MuiSlider-valueLabel': {
    left: '-50%',
  },
}));

const CustomTooltip = styled(({ className, ...props }: React.ComponentProps<typeof Tooltip>) => (
  <Tooltip arrow {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'rgba(0,0,0,0.8)',
    fontWeight: 700,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: 'rgba(0,0,0,0.8)',
  },
}));

// Value label component for the slider
const ValueLabelComponent: FunctionComponent<{
  children: React.ReactElement;
  open: boolean;
  value: number;
}> = ({ children, open, value }) => (
  <CustomTooltip open={open} placement="top" title={`${value}%`}>
    {children}
  </CustomTooltip>
);

// Props interface
interface IProps {
  id: string;
  label: string;
  value: string;
  errorMessage?: string;
  hideInput?: boolean;
  onInputChange?: (
    e: React.FormEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onSliderChange?: (value: number | number[]) => void;
}

const Slider: FunctionComponent<IProps> = ({
  id,
  label,
  value,
  errorMessage,
  onInputChange,
  onSliderChange,
  hideInput = false,
}) => {
  const styles = useStyles();

  const handleSliderChange = (
    event: Event | React.SyntheticEvent,
    newValue: number | number[]
  ) => {
    onSliderChange?.(newValue);
  };

  return (
    <div className={styles.container}>
      <Typography className={styles.label}>{label}</Typography>
      <div className={styles.sliderWrapper}>
        <CustomSlider
          value={Number(value) || 0}
          onChange={handleSliderChange}
          valueLabelDisplay="auto"
          defaultValue={0}
          slots={{
            valueLabel: ValueLabelComponent,
          }}
          valueLabelFormat={(val) => `${val}`}
        />
        {!hideInput && (
          <InputText
            id={id}
            name={id}
            value={value}
            onChange={onInputChange}
            type={Constants.INPUT_TYPES.PERCENTAGE}
            errorMessage={errorMessage}
            hideErrorMessage={true}
            customClass={styles.input}
          />
        )}
      </div>
      {errorMessage &&
        errorMessage !== Constants.FORM_ERRORS.REQUIRED_FIELD_ERROR && (
          <Typography className={styles.errorMessage}>
            {errorMessage}
          </Typography>
        )}
    </div>
  );
};

export default Slider;